import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Chip,
  Divider,
  Grid,
  Typography,
  Paper,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { encryptId } from "../../utils/encryption";

import { ThemeProvider, createTheme, styled } from "@mui/material/styles";
import JobCard from "views/Landing/JobCard";
import arrowBack from "../../assets/img/Vector.svg";
import applyArrow from "../../assets/img/apply_arrow.svg";
import saveIcon from "../../assets/img/save_icon.svg";
import saveFilledIcon from "../../assets/img/save_filled_icon.svg";
import shareIcon from "../../assets/img/share_icon.svg";
import treePoints from "../../assets/img/tree_points.svg";
import colors from "colors/color";
import { useNavigate } from "react-router-dom";
import { useSavePost, useSaveStatus } from "./hooks/useSavePost";
import SharePostLink from "../Posts/SharePostLink";
import { useTranslation } from "react-i18next";
import smart_logo_white from "../../assets/img/smart_logo_white.svg";
import smart_logo from "../../assets/img/small_logo.svg";
import mail_logo from "../../assets/img/mail_icon.svg";
import mail_logo_white from "../../assets/img/email_logo_white.svg";
import useUserId from "hooks/useUserId";

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  backgroundColor: "#D9D9D933",
  width: "100%",
  height: "100%",
  minHeight: 120,
  borderRadius: "20px",
  border: "1px solid",
  borderColor: "#D9D9D9",
  padding: "19px 20px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
}));

const TypographyCardContent = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "26px",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
}));

const TypographyTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "24px",
  color: colors.primary,
  marginBottom: 7,
}));
const TypographySubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "100%",
  letterSpacing: "0%",
  color: colors.primary,
  marginBottom: 7,
}));
const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  minWidth: "59px",
  minHeight: "59px",
  maxWidth: "59px",
  maxHeight: "59px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",

  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
}));
const SaveButton = styled(Button)(({ theme, variant, issaved }) => ({
  maxWidth: 1,
  width: 1,
  height: 44,
  borderRadius: "11px",
  borderWidth: "1px",
  border: "1px solid #0017E7",
  color: "#0017E7",
  backgroundColor: issaved === "true" ? "#0017E7" : "transparent",
  "&:hover": {
    backgroundColor: issaved === "true" ? "#0017E7" : "rgba(61, 90, 254, 0.04)",
    borderColor: "#3D5AFE",
  },
  padding: "0",
}));

const theme = createTheme({
  palette: {
    primary: {
      main: "#0017e7",
    },
    secondary: {
      main: "#ffbe00",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Add these utility functions at the top of the file, after imports
export const calculateTimeSincePosting = (postDate, t) => {
  try {
    console.log("=== DETAILED DATE DEBUGGING ===");
    console.log("1. Input postDate:", postDate);
    console.log("1.1 Input postDate type:", typeof postDate);

    if (!postDate) {
      console.log("No postDate provided");
      return t("jobCard.timeAgo.justNow");
    }

    // Get user's timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("2. User timezone:", userTimeZone);

    // Get current time in user's timezone
    const now = new Date();
    console.log("3. Current time details:");
    console.log("3.1 Current time (ISO):", now.toISOString());
    console.log("3.2 Current time (local):", now.toString());

    // Parse the post date and convert to user's timezone
    let postDateTime;
    if (typeof postDate === "string") {
      // Check if it's an ISO datetime string (contains T or Z)
      if (postDate.includes("T") || postDate.includes("Z")) {
        // Parse ISO datetime string
        postDateTime = new Date(postDate);
      } else if (postDate.includes(" ")) {
        // Handle space-separated datetime
        const [datePart, timePart] = postDate.split(" ");
        const [year, month, day] = datePart.split("-").map(Number);
        const [hours, minutes, seconds] = timePart.split(":").map(Number);
        postDateTime = new Date(year, month - 1, day, hours, minutes, seconds);
      } else {
        // Handle date-only format (YYYY-MM-DD)
        const [year, month, day] = postDate.split("-").map(Number);
        postDateTime = new Date(year, month - 1, day, 0, 0, 0);
      }
    } else {
      postDateTime = new Date(postDate);
    }

    // Check if the post date is in the future
    const isFuture = postDateTime > now;
    if (isFuture) {
      return t("jobCard.timeAgo.justNow");
    }

    // Calculate time differences
    const diffMs = now - postDateTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Return the appropriate translation
    if (diffSeconds < 60) {
      return t("jobCard.timeAgo.justNow");
    } else if (diffMinutes < 60) {
      return t("jobCard.timeAgo.minutesAgo", { count: diffMinutes });
    } else if (diffHours < 24) {
      return t("jobCard.timeAgo.hoursAgo", { count: diffHours });
    } else if (diffDays < 7) {
      return t("jobCard.timeAgo.daysAgo", { count: diffDays });
    } else if (diffWeeks < 4) {
      return t("jobCard.timeAgo.weeksAgo", { count: diffWeeks });
    } else if (diffMonths < 12) {
      return t("jobCard.timeAgo.monthsAgo", { count: diffMonths });
    } else {
      return t("jobCard.timeAgo.yearsAgo", { count: diffYears });
    }
  } catch (error) {
    console.error("Error calculating time since posting:", error);
    return t("jobCard.timeAgo.justNow");
  }
};

const calculateDaysUntilExpiration = (deadline) => {
  try {
    if (!deadline) return { value: 0, unit: "days" };

    const now = new Date();
    const deadlineDate = new Date(deadline);

    // Check if the date is valid
    if (isNaN(deadlineDate.getTime())) {
      return { value: 0, unit: "days" };
    }

    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Calculate more precise time differences
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30.44); // Average days in a month
    const diffYears = Math.floor(diffDays / 365.25); // Account for leap years

    if (diffDays <= 0) {
      return { value: 0, unit: "days" };
    } else if (diffDays < 7) {
      return { value: diffDays, unit: "days" };
    } else if (diffDays < 30) {
      return { value: diffWeeks, unit: "weeks" };
    } else if (diffDays < 365) {
      return { value: diffMonths, unit: "months" };
    } else {
      return { value: diffYears, unit: "years" };
    }
  } catch (error) {
    console.error("Error calculating days until expiration:", error);
    return { value: 0, unit: "days" };
  }
};

export default function Details({
  job,
  onClose,
  posts = [],
  refresh,
  setSelectedJob,
  setOpenDialog,
}) {
  const { t } = useTranslation();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [similarPosts, setSimilarPosts] = useState([]);
  const [loadingSimilarPosts, setLoadingSimilarPosts] = useState(false);
  const user = useUserId();
  const userId = user.userId;
  const [isExpanded, setIsExpanded] = useState(false);
  const isAuth = localStorage.getItem("accessToken");
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  // Add this debug log
  useEffect(() => {
    console.log("Job object:", job);
    console.log("Job date:", job?.date);
  }, [job]);

  // Fetch similar posts when job changes
  useEffect(() => {
    const fetchSimilarPosts = async () => {
      if (!job?.title) {
        console.log("No job title available for similar posts search");
        setSimilarPosts([]);
        return;
      }

      setLoadingSimilarPosts(true);
      try {
        console.log("🔍 Fetching similar posts for title:", job.title);
        const response = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }candidates/search_recommendation_by_title/?title=${encodeURIComponent(
            job.title
          )}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("✅ Similar posts response:", data);

        // Extract posts from the response structure
        const extractedPosts = data.results?.map((item) => item.post) || [];

        // Filter out the current job from similar posts
        const filteredPosts = extractedPosts.filter(
          (post) => post.id !== job.id
        );

        setSimilarPosts(filteredPosts);
        console.log(
          `📊 Found ${filteredPosts.length} similar posts out of ${extractedPosts.length} total results`
        );
      } catch (error) {
        console.error("❌ Error fetching similar posts:", error);
        // Fallback to using the original posts prop
        setSimilarPosts(
          posts.filter((post) => post.id !== job.id).slice(0, 10)
        );
      } finally {
        setLoadingSimilarPosts(false);
      }
    };

    fetchSimilarPosts();
  }, [job?.title, job?.id, posts]);

  // Add detailed logging of the job object
  useEffect(() => {
    console.log("=== Job Details Debug ===");
    console.log("Full job object:", job);
    console.log("Job date:", job?.date);
    console.log("Job date type:", typeof job?.date);
    console.log("Job deadline:", job?.deadline);
    console.log("Job deadline type:", typeof job?.deadline);

    // Add new debug logs for the fields we added
    console.log("=== New Fields Debug ===");
    console.log("Education:", job?.education);
    console.log("Highest Diploma:", job?.highest_diploma);
    console.log("Field of Study:", job?.field_of_study);
    console.log("Experiences:", job?.experiences);
    console.log("Offer Type:", job?.offerType);

    // Log the structure of the job object
    console.log("=== Job Object Structure ===");
    console.log("Available keys:", Object.keys(job || {}));
  }, [job]);

  // Always call useSaveStatus, but with a safe jobId
  const jobId = job?.id || "";

  const { isLoading, error, toggleSaveStatus, isSaved } = useSaveStatus({
    jobId,
    onSaveChange: (newStatus) => {
      if (job) {
        console.log(`Job ${jobId} is now ${newStatus ? "saved" : "unsaved"}`);
      }
    },
  });
  const navigate = useNavigate();

  //   const [isSaved, setIsSaved] = useState(job?.isSaved || false);
  useEffect(() => {
    console.log("Save status:", isSaved);
  }, [isSaved]);

  // Calculate time values with deadline as fallback
  const timeSincePosting = calculateTimeSincePosting(job?.date, t);
  const daysUntilExpiration = calculateDaysUntilExpiration(job?.deadline);

  console.log("=== Calculated Values ===");
  console.log("Time since posting:", timeSincePosting);
  console.log("Days until expiration:", daysUntilExpiration);

  if (!job) {
    console.log("No job data available");
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "85vh",
          color: "text.secondary",
        }}
      >
        <Typography variant="h6">Job not found or has been removed</Typography>
      </Container>
    );
  }

  // Add loading state
  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "85vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // Add safe access to nested properties with proper type checking
  const requirements = Array.isArray(job.requirements)
    ? job.requirements.filter(
        (req) =>
          typeof req === "string" ||
          (typeof req === "object" &&
            req !== null &&
            Object.keys(req).length > 0)
      )
    : [];
  const salary = job.salary || "";
  const jobType = job.offerType || "";
  const mode = job.mode || "";

  // Log the processed values
  console.log("Processed values:", {
    requirements,
    salary,
    jobType,
    mode,
  });

  const getUserResume = async () => {
    if (userId) {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}authentication/get-user-resume/${userId}/`
      );
      const data = await response.json();
      console.log("User resume:", data);
      return data;
    }
    return null;
  };

  const sendMail = async (
    email,
    title,
    description,
    accountType,
    reference
  ) => {
    console.log("Sending mail to:", email);
    console.log("Title:", title);
    console.log("Description:", description);

    // Get user resume data
    const resumeData = await getUserResume();

    let mailtoUrl = "";
    const subject = reference || title;

    // Create a professional email body
    let body = `Dear Recruiter,\n\nI am writing to express my strong interest in the ${title} position${
      reference ? ` (Reference: ${reference})` : ""
    } at your company.\n\n`;

    // Add resume information if available
    if (resumeData && resumeData.status === "success") {
      // Remove trailing slash from API URL and leading slash from resume_url
      const apiUrl = process.env.REACT_APP_API_URL.replace(/\/$/, "");
      const resumePath = resumeData.resume_url.replace(/^\//, "");
      const resumeUrl = `${apiUrl}/${resumePath}`;

      body += `My Details:\n`;
      body += `- Name: ${resumeData.name}\n`;
      body += `- Resume: ${resumeUrl}\n\n`;
    }

    body += `I came across this opportunity through your job posting and I am very excited about the possibility of joining your team. I believe my skills and experience align well with the requirements of this position.\n\n`;

    body += `Job Details:\n`;
    body += `- Position: ${title}\n`;
    if (reference) body += `- Reference Number: ${reference}\n`;
    if (mode) body += `- Work Mode: ${mode}\n`;

    body += `\nBest regards,\n`;
    if (resumeData && resumeData.status === "success") {
      body += resumeData.name;
    }

    if (accountType === "m") {
      // Microsoft Outlook
      mailtoUrl = `https://outlook.office.com/mail/deeplink/compose?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}&to=${encodeURIComponent(email)}`;
    } else {
      // Gmail
      mailtoUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
        email
      )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    window.open(mailtoUrl, "_blank");
  };

  const handleApply = () => {
    console.log("Applying for job:", job);
    if (job.scraped) {
      sendMail(
        job.recruiter_email,
        job.title,
        job.description,
        job.accountType,
        job.reference
      );
    } else {
      navigate("/application_page", { state: { job } });
    }
  };

  const handleShare = () => {
    console.log("Share button clicked in Details");
    console.log("Current job:", job);

    if (!job || !job.id) {
      console.error("Missing job data for sharing");
      return;
    }

    // Encrypt the job ID before creating the URL
    const encryptedId = encryptId(job.id);
    if (!encryptedId) {
      console.error("Failed to encrypt job ID");
      return;
    }

    // Generate URL with encrypted ID
    const url = `${window.location.origin}/jobs/${encryptedId}`;
    setShareUrl(url);
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="xl"
        sx={{ bgcolor: "background.default", minHeight: "auto", py: 4, ml: 6 }}
      >
        <Grid container spacing={4}>
          {/* Main Content - Left Side */}
          <Grid item xs={12} lg={7}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Back Button */}
              <Button
                variant="outlined"
                color="primary"
                onClick={onClose}
                sx={{
                  alignSelf: "flex-start",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                }}
              >
                <img src={arrowBack} width={13} height={13} alt="Back" />
                <Typography sx={{ marginLeft: 1 }}>
                  {t("details.backToJobs")}
                </Typography>
              </Button>

              {/* Job Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <CompanyLogo logo={job?.cp_logo_url }>
                <img
                  src={job?.cp_logo_url}
                  alt="Company Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </CompanyLogo>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        fontSize: "24px",
                        lineHeight: "36px",
                        mb: 1,
                      }}
                    >
                      {job.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "100%",
                        color: "#00000080",
                        mb: 2,
                      }}
                    >
                      {t("details.posted")} {timeSincePosting}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "100%",
                        color: "#00000080",
                        mb: 1,
                      }}
                    >
                      {job.company}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "100%",
                        color: "#00000080",
                        mb: 1,
                      }}
                    >
                      {job.localisation}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    onClick={handleApply}
                    sx={{
                      backgroundColor: colors.primary,
                      width: 136,
                      height: 50,
                      borderRadius: "11px",
                      padding: "14px 16px",
                      gap: "5px",
                      "&:hover": {
                        border: `1px solid ${colors.primary}`,
                        backgroundColor: "#0017E70D",
                        "& .MuiTypography-root": {
                          color: colors.primary,
                        },
                        "& .smart-logo": {
                          content: `url(${smart_logo})`,
                        },
                        "& .mail-logo": {
                          content: `url(${mail_logo})`,
                        },
                      },
                    }}
                  >
                    <img
                      src={job.scraped ? mail_logo_white : smart_logo_white}
                      className={!job.scraped ? "smart-logo" : "mail-logo"}
                      height={16}
                      width={16}
                      style={{ transition: "all 0.3s" }}
                      alt="Apply"
                    />
                    <Typography
                      sx={{
                        color: "white",
                        fontFamily: "Poppins",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "100%",
                        ml: 0.2,
                        transition: "color 0.3s",
                      }}
                    >
                      {t("myCard.smartApply")}
                    </Typography>
                  </Button>
                  {isAuth &&
                  <IconButton
                    onClick={toggleSaveStatus}
                    variant="outlined"
                    sx={{
                      width: 50,
                      height: 50,
                      borderColor: colors.primary,
                      borderRadius: "11px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      gap: "10px",
                      backgroundColor: isSaved ? "#0017E7" : "transparent",
                      "&:hover": {
                        backgroundColor: isSaved
                          ? "#0017E7"
                          : "rgba(61, 90, 254, 0.04)",
                        borderColor: "#3D5AFE",
                      },
                    }}
                  >
                    <img
                      src={!isSaved ? `${saveIcon}` : `${saveFilledIcon}`}
                      width={10}
                      height={16}
                      style={{
                        filter: isSaved ? "brightness(0) invert(1)" : "none",
                      }}
                    />{" "}
                  </IconButton>
                  }
                  
                  <IconButton
                    onClick={handleShare}
                    variant="outlined"
                    sx={{
                      width: 50,
                      height: 50,
                      borderColor: colors.primary,
                      borderRadius: "11px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      gap: "10px",
                    }}
                  >
                    <img src={shareIcon} height={14} width={14} />
                  </IconButton>
                </Box>
              </Box>

              {/* Expiration Date */}
              <Box>
                <TypographyTitle>{t("details.expirationDate")}</TypographyTitle>
                <Chip
                  label={(() => {
                    if (daysUntilExpiration.unit === "years") {
                      return t(
                        daysUntilExpiration.value === 1
                          ? "jobCard.deadline.yearsToExpire"
                          : "jobCard.deadline.yearsToExpire_plural",
                        { count: daysUntilExpiration.value }
                      );
                    } else if (daysUntilExpiration.unit === "months") {
                      return t(
                        daysUntilExpiration.value === 1
                          ? "jobCard.deadline.monthsToExpire"
                          : "jobCard.deadline.monthsToExpire_plural",
                        { count: daysUntilExpiration.value }
                      );
                    } else if (daysUntilExpiration.unit === "weeks") {
                      return t(
                        daysUntilExpiration.value === 1
                          ? "jobCard.deadline.weeksToExpire"
                          : "jobCard.deadline.weeksToExpire_plural",
                        { count: daysUntilExpiration.value }
                      );
                    } else {
                      return t(
                        daysUntilExpiration.value === 1
                          ? "jobCard.deadline.daysToExpire"
                          : "jobCard.deadline.daysToExpire_plural",
                        { count: daysUntilExpiration.value }
                      );
                    }
                  })()}
                  variant="outlined"
                  color={
                    daysUntilExpiration.value < 7 &&
                    daysUntilExpiration.unit === "days"
                      ? "warning"
                      : "primary"
                  }
                  sx={{
                    px: 1,
                    backgroundColor:
                      daysUntilExpiration.value < 7 &&
                      daysUntilExpiration.unit === "days"
                        ? "#FBBC0533"
                        : "#0017E70D",
                    width: "auto",
                    minWidth: 181,
                    height: 41,
                    borderRadius: "12px",
                    border: "0.3px solid",
                    borderColor:
                      daysUntilExpiration.value < 7 &&
                      daysUntilExpiration.unit === "days"
                        ? "#FBBC05"
                        : "divider",
                    padding: "10px 30px",
                    gap: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    "& .MuiChip-label": {
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "13px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      padding: 0,
                      color:
                        daysUntilExpiration.value < 7 &&
                        daysUntilExpiration.unit === "days"
                          ? "#FBBC05"
                          : "inherit",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Box>

              {/* Description */}
              <Box>
                <TypographyTitle>{t("details.description")}</TypographyTitle>
                <Stack spacing={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      // mb: 2,
                      color: "#000000",
                      fontSize: "14px",
                      lineHeight: "26px",
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      display: isExpanded ? "block" : "-webkit-box",
                      WebkitLineClamp: isExpanded ? "none" : 8,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      "& p": {
                        margin: 0,
                        lineHeight: "26px",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#000000",
                      },
                      "& strong": {
                        fontWeight: 400,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: job.description }}
                  />
                  <Button
                    onClick={toggleExpanded}
                    sx={{
                      mt: 1,
                      p: 0,
                      minWidth: "auto",
                      color: "#1976d2",
                      fontSize: "14px",
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {isExpanded ? t("See_less") : t("See_more")}
                  </Button>
                </Stack>
              </Box>

              {/* Additional Information */}
              <Box>
                {/* <TypographyTitle>{t("details.additionalInfo")}</TypographyTitle> */}
                <Grid container spacing={2}>
  <Grid item xs={12}>
    <CardContentStyled
      variant="outlined"
      sx={{
        backgroundColor: "#D9D9D933",
        width: "100%",
        minHeight: 96,
        borderRadius: "20px",
        border: "1px solid",
        borderColor: "divider",
        padding: "19px 30px 19px 20px",
        gap: "10px",
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <CardContent sx={{ width: "100%", padding: 0 }}>
        {/* <TypographySubTitle>
          {t("details.jobDetails")}
        </TypographySubTitle> */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            wordBreak: "break-word",
          }}
        >
          {/* Required Skills */}
          {requirements.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.requiredSkills")}:</strong>{" "}
                {requirements
                  .map((skill) =>
                    typeof skill === "string" ? skill : JSON.stringify(skill)
                  )
                  .join(", ")}
              </TypographyCardContent>
            </Box>
          )}

          {/* Salary */}
          {salary && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.salary")}:</strong> {salary} {t("details.perMonth")}
              </TypographyCardContent>
            </Box>
          )}

          {/* Job Type */}
          {jobType && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.jobType")}:</strong>{" "}
                {jobType === "i"
                  ? t("details.internship")
                  : jobType === "j"
                  ? t("details.job")
                  : jobType}
              </TypographyCardContent>
            </Box>
          )}

          {/* Mode */}
          {mode && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.mode")}:</strong> {mode}
              </TypographyCardContent>
            </Box>
          )}

          {/* Education */}
          {job.education && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.education")}:</strong> {job.education}
              </TypographyCardContent>
            </Box>
          )}

          {/* Highest Diploma */}
          {job.highest_diploma && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.highestDiploma")}:</strong> {job.highest_diploma}
              </TypographyCardContent>
            </Box>
          )}

          {/* Field of Study */}
          {job.field_of_study && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.fieldOfStudy")}:</strong> {job.field_of_study}
              </TypographyCardContent>
            </Box>
          )}

          {/* Offer Type */}
          {job.offerType && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "text.primary",
                  marginTop: 0.75,
                  flexShrink: 0,
                }}
              />
              <TypographyCardContent>
                <strong>{t("details.offerType")}:</strong>{" "}
                {job.offerType === "i"
                  ? t("details.internship")
                  : job.offerType === "j"
                  ? t("details.job")
                  : job.offerType}
              </TypographyCardContent>
            </Box>
          )}
        </Box>
      </CardContent>
    </CardContentStyled>
  </Grid>
</Grid>
              </Box>
            </Box>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TypographyTitle>{t("details.mightInterestYou")}</TypographyTitle>
              <Box
                sx={{
                  height: "1200px",
                  overflowY: "auto",
                  padding: "10px",
                }}
              >
                {loadingSimilarPosts ? (
                  // Loading state for similar posts
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : similarPosts.length > 0 ? (
                  // Display similar posts
                  similarPosts.map((job, index) => (
                    <JobCard
                      key={`similar-${job.id}-${index}`}
                      job={job}
                      setSelectedJob={setSelectedJob}
                      setOpenDialog={setOpenDialog}
                      refresh={refresh}
                    />
                  ))
                ) : (
                  // Fallback to general posts if no similar posts found
                  posts
                    .filter((post) => post.id !== job?.id)
                    .map((job, index) => (
                      <JobCard
                        key={`fallback-${job.id}-${index}`}
                        job={job}
                        setSelectedJob={setSelectedJob}
                        setOpenDialog={setOpenDialog}
                        refresh={refresh}
                      />
                    ))
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <SharePostLink
          open={shareDialogOpen}
          onClose={handleCloseShareDialog}
          shareUrl={shareUrl}
          title={t("jobCard.shareOptions.title")}
        />
      </Container>
    </ThemeProvider>
  );
}
